import { isEmpty } from 'lodash';
import { normalizeEvoRegion } from 'constants/evoRegions';
import { PROVIDER_ACCESS } from 'constants/identityProviders';
import {
  GetIdentityProviderResponse,
  GetIdentityProviderParams,
} from 'types/sessionsTypes';
import {
  redirectToVcIdentity,
  redirectToVcIdentityError,
  redirectToVcLogout,
  redirectToAccessUrl,
  ensureCodeVerifier,
  buildAuthorizationCallbackUrl,
  generateCodeChallenge,
  pickRedirectByRegion,
  parseIdToken,
} from 'utils/sessions';
import {
  setUserIdTokenToLocalStorage,
  setEmailToLocalStorage,
  setIdentityProviderToLocalStorage,
  getIdentityProviderFromLocalStorage,
} from 'utils/storages';
import { toApiError } from 'utils/errors';

export const isAccessFederatedUser = (): boolean =>
  getIdentityProviderFromLocalStorage()?.provider === PROVIDER_ACCESS;

export const buildIdentityProviderRequest = ({
  email,
  search,
}: {
  email: string;
  search: string;
}): GetIdentityProviderParams => {
  const codeVerifier = ensureCodeVerifier();
  const codeChallenge = generateCodeChallenge(codeVerifier);

  return {
    email,
    continue: buildAuthorizationCallbackUrl(search),
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
  };
};

export const resolveAccessRedirectUri = (
  response?: GetIdentityProviderResponse | null,
  region?: string | null
): string | null | undefined => {
  const { redirectUri, redirects } =
    response || ({} as GetIdentityProviderResponse);
  return isEmpty(redirectUri)
    ? pickRedirectByRegion(redirects, region)?.uri
    : redirectUri;
};

export const handleAccessSsoLoginResponse = (
  response: GetIdentityProviderResponse | null | undefined,
  { email, evoRegion, search }: { email: string; evoRegion?: string | null; search: string }
): void => {
  const { provider } = response || ({} as GetIdentityProviderResponse);

  if (!isEmpty(provider)) {
    setIdentityProviderToLocalStorage(email, provider);
  }

  if (provider !== PROVIDER_ACCESS) {
    redirectToVcIdentity(search);
    return;
  }

  const uri = resolveAccessRedirectUri(response, normalizeEvoRegion(evoRegion));
  if (isEmpty(uri) || !redirectToAccessUrl(uri)) {
    redirectToVcIdentity(search);
  }
};

export const handleIdentityProviderError = (
  error: unknown,
  { email, search }: { email: string; search: string }
): void => {
  const { status, data } = toApiError(error);
  if (status === 400 && data?.errorCode) {
    redirectToVcIdentityError({
      email,
      error_code: data.errorCode,
    });
  } else {
    redirectToVcIdentity(search);
  }
};

export const finishLogoutRedirect = ({
  accessLogoutUri,
  mfaEnabled,
}: {
  accessLogoutUri?: string | null;
  mfaEnabled: boolean;
}): void => {
  if (!redirectToAccessUrl(accessLogoutUri)) {
    if (mfaEnabled) {
      redirectToVcLogout();
    } else {
      redirectToVcIdentity();
    }
  }
};

export const persistIdTokenSession = (idToken: string | null): void => {
  if (isEmpty(idToken)) {
    throw new Error('Invalid Id token');
  }
  setUserIdTokenToLocalStorage(idToken);
  const tokenInfo = parseIdToken(idToken);
  setEmailToLocalStorage(tokenInfo['email']);
};

export const handleGetIdTokenError = (error: unknown): void => {
  const { status, data } = toApiError(error);
  if (status === 400 && data?.errorCode) {
    redirectToVcIdentityError({
      email: data.error,
      error_code: data.errorCode,
    });
  } else {
    redirectToVcIdentity();
  }
};
